import React, { FC, useState, useEffect, useRef, useCallback } from "react";
import { AddressSuggestion } from "../../../../../../infrastructure/http/modules/adressevaelger/models";
import { addressFetch, addressDetailsFetch } from "../../../../../../infrastructure/http/modules/adressevaelger/fetch";
import { Address } from "../../../../../../infrastructure/http/modules/cart/cart.models";
import useDebounce from '../../../../../common/useDebounce';
import { EventKeysNames } from "../../../../../../infrastructure/const";

type AdressevaelgerProps = {
    label: string,
    addressSearchText: string,
    onSelect: (address: Address) => void;
};

const AdressevaelgerAutocomplete: FC<AdressevaelgerProps> = ({
  label,
  addressSearchText,
  onSelect,
}) => {

    const elRef = useRef<HTMLDivElement>(null);
    const minCharacters = 2;
    const addressTypeName = "adresse";
    //This regions const will be changed
    const regions: Record<string, string> = {
        "0101": "Capital",
        "0751": "Central",
        "0851": "North",
        "0461": "South",
        "0561": "South",
        "0630": "South",
        "0147": "Capital",
        "0730": "Central",
        "0791": "Central",
        "0621": "South",
        "0740": "Central",
        "0657": "Central",
        "0615": "Central",
        "0265": "Zealand",
        "0370": "Zealand",
        "0330": "Zealand",
        "0540": "South",
        "0157": "Capital",
        "0316": "Zealand",
        "0860": "North",
        "0159": "Capital",
        "0376": "Zealand",
        "0217": "Capital",
        "0813": "North",
        "0580": "South",
        "0479": "South",
        "0746": "Central",
        "0760": "Central",
        "0259": "Zealand",
        "0661": "Central",
        "0510": "South",
        "0230": "Capital",
        "0173": "Capital",
        "0430": "South",
        "0167": "Capital",
        "0573": "South",
        "0607": "South",
        "0326": "Zealand",
        "0219": "Capital",
        "0169": "Capital",
        "0151": "Capital",
        "0253": "Zealand",
        "0779": "Central",
        "0710": "Central",
        "0766": "Central",
        "0390": "Zealand",
        "0360": "Zealand",
        "0787": "North",
        "0250": "Capital",
        "0575": "South",
        "0846": "North",
        "0240": "Capital",
        "0706": "Central",
        "0420": "South",
        "0400": "Capital",
        "0185": "Capital",
        "0756": "Central",
        "0270": "Capital",
        "0210": "Capital",
        "0550": "South",
        "0849": "North",
        "0190": "Capital",
        "0707": "Central",
        "0410": "South",
        "0820": "North",
        "0175": "Capital",
        "0810": "North",
        "0320": "Zealand",
        "0153": "Capital",
        "0329": "Zealand",
        "0306": "Zealand",
        "0450": "South",
        "0260": "Capital",
        "0340": "Zealand",
        "0480": "South",
        "0840": "North",
        "0165": "Capital",
        "0350": "Zealand",
        "0163": "Capital",
        "0530": "South",
        "0223": "Capital",
        "0201": "Capital",
        "0440": "South",
        "0671": "Central",
        "0336": "Zealand",
        "0727": "Central",
        "0161": "Capital",
        "0773": "North",
        "0665": "Central",
        "0269": "Zealand",
        "0183": "Capital",
        "0187": "Capital",
        "0155": "Capital",
        "0482": "South",
        "0492": "South",
        "0741": "Central",
        "0563": "South",
        "0825": "North"
    };

    const [addressSearchInput, setAddressSearchInput] = useState<string>(addressSearchText);
    const [addressSearchLastSelect, setAddressSearchLastSelect] = useState<string>(addressSearchText);
    const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
    const [suggestionIndex, setSuggestionIndex] = useState<number>(-1);
    const debouncedAddressSearchInput = useDebounce(addressSearchInput, 300);


    const handleClickOutside = useCallback(
        (evt: MouseEvent) => {
            if (elRef.current && !elRef.current.contains(evt.target as Node)) {
                setAddressSearchInput(addressSearchLastSelect);
                setSuggestions([]);
                setSuggestionIndex(-1);  
            }
        },
        [addressSearchLastSelect, elRef]
    );

    useEffect(() => {
        document.addEventListener("click", handleClickOutside, true);
        
        return () => {
            document.removeEventListener("click", handleClickOutside, true);
        };
    }, [handleClickOutside]);
    

    const handleInputChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
        const { value } = event.currentTarget;    
        
        if (minCharacters <= value.length && addressSearchInput !== value) {
            setAddressSearchInput(value);
            setSuggestionIndex(-1);
        }
        else{
            onSelect(new Address());

            setAddressSearchInput(value);
            setAddressSearchLastSelect('');
            setSuggestions([]);
            setSuggestionIndex(-1);   
        }
    }

    const getSuggestions = useCallback(
        (value: string): void => {
            addressFetch(
                value,
                (suggestions: AddressSuggestion[]) => {
                    setSuggestions(suggestions);
                }
            )        
        },
        []
    );

    useEffect(() => {
        if (debouncedAddressSearchInput === addressSearchInput && addressSearchLastSelect !== addressSearchInput  && addressSearchInput.length >= minCharacters) {
            getSuggestions(addressSearchInput);
        }
    }, [getSuggestions, addressSearchInput, addressSearchLastSelect, debouncedAddressSearchInput]);


    const suggestionSelect = async (suggestion: AddressSuggestion) => {
        suggestionAddressSelect(suggestion);            
    }

    const suggestionAddressSelect = async (suggestion: AddressSuggestion) => {   

        if(!suggestion.husnummerId || suggestion.type !== addressTypeName) {
            suggestionAddressSearch(suggestion);
        }
        else {
            suggestionAddressSet(suggestion);
        }
    }

    const suggestionAddressSearch = async (suggestion: AddressSuggestion) => {   

        setAddressSearchInput(suggestion.titel);
        setSuggestions([]);
        setSuggestionIndex(-1); 
    }

    const suggestionAddressSet = async (suggestion: AddressSuggestion) => {   
        const addressDetails = await addressDetailsFetch(suggestion.id);
        if(addressDetails != null) {

            const addressLine = [];
            addressLine.push(addressDetails.husnummer.vejnavn);
            if(addressDetails.husnummer.husnummertekst) {
                addressLine.push(addressDetails.husnummer.husnummertekst)
            }

            const addressLine2 = [];
            if(addressDetails.etagebetegnelse) {
                addressLine2.push(addressDetails.etagebetegnelse)
            }
            if(addressDetails.doerbetegnelse) {
                addressLine2.push(addressDetails.doerbetegnelse)
            }
            
            let address = new Address();
            address.addressApiText = addressDetails.adressebetegnelse;
            address.addressLine1 = addressLine.join(' ');
            address.addressLine2 = addressLine2.join(' ');
            address.city = addressDetails.husnummer.postnummer.navn;
            address.postalCode = addressDetails.husnummer.postnummer.postnr;
            address.county = regions[addressDetails.husnummer.navngivenvejkommunedel.kommune];    

            onSelect(address);
            setAddressSearchInput(suggestion.titel);
            setAddressSearchLastSelect(suggestion.titel);
            setSuggestions([]);
            setSuggestionIndex(-1); 
        }
        else {
            onSelect(new Address());
            setAddressSearchInput('');
            setAddressSearchLastSelect('');
            setSuggestions([]);
            setSuggestionIndex(-1); 
        }
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if(suggestions.length === 0) {
            return;
        }

        switch(event.key){
            case EventKeysNames.Escape:
                setAddressSearchInput(addressSearchLastSelect)
                setSuggestions([]);
                setSuggestionIndex(-1);   
                break;
            case EventKeysNames.Enter:
                if(suggestionIndex >= 0 && suggestionIndex < suggestions.length) {
                    const suggestion = suggestions[suggestionIndex];
                    suggestionSelect(suggestion);
                }
                break;
            case EventKeysNames.ArrowUp:
                setSuggestionIndex(suggestionIndex <= 0 ? suggestions.length - 1 : suggestionIndex - 1);  
                break;
            case EventKeysNames.ArrowDown:
                setSuggestionIndex(suggestionIndex < suggestions.length - 1 ? suggestionIndex + 1 : 0); 
                break;
        }
    }

    return <div ref={elRef}>
        <label className="required">{label}<span className="required"> *</span></label>
        <input type="text" value={addressSearchInput} onChange={handleInputChange} onKeyDown={handleKeyDown} />
        {suggestions.length > 0 &&
        <ul className="adressevaelger-autocomplete">
            {suggestions.map((suggestion, index) => (
                <li key={suggestion.id} 
                    className={index === suggestionIndex? "selected" : ""}
                    onClick={() => { suggestionSelect(suggestion); }}>
                        {suggestion.titel}
                </li>
            ))}
        </ul>}
    </div>
};

export default AdressevaelgerAutocomplete;